import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { Search, Zap, MessageSquare, BarChart3, Users, Settings, Activity, Shield, Clock, Star, CheckCircle, ArrowRight, KeyRound, Plug, Lock, Globe, Terminal, BoxIcon } from "lucide-react";
import { ClosingCTA } from "../components/ClosingCTA";
import { Markdown } from "../components/Markdown";
import { fetchIntegration, getCachedIntegration, IntegrationDetail as Detail, CatalogMethod } from "../lib/catalog";
import { trackSignupClick, withAnalyticsParams } from "../lib/analytics";
import { IntegrationLogo, GIGA_MARK } from "../components/IntegrationLogo";
import { TALK_TO_SALES_URL } from "../lib/links";
// Map a lucide icon NAME (stored in page_content) to a component, with a
// safe fallback so unknown names never crash the page.
const ICONS: Record<string, BoxIcon> = {
  Search,
  Zap,
  MessageSquare,
  BarChart3,
  Users,
  Settings,
  Activity,
  Shield,
  Clock,
  Star,
  CheckCircle
};
function iconFor(name?: string): BoxIcon {
  return name && ICONS[name] || Zap;
}

// Friendly labels + icons for the auth mechanisms we support.
const METHOD_META: Record<string, {
  label: string;
  icon: BoxIcon;
  blurb: string;
}> = {
  oauth2: {
    label: 'One-click connect',
    icon: Plug,
    blurb: 'Secure OAuth — log in and authorize, no keys to copy.'
  },
  mcp: {
    label: 'MCP server',
    icon: Plug,
    blurb: 'Structured tool access via the Model Context Protocol.'
  },
  api_key: {
    label: 'API key',
    icon: KeyRound,
    blurb: 'Paste an API key — Giga stores it encrypted.'
  },
  pat: {
    label: 'Personal access token',
    icon: KeyRound,
    blurb: 'Connect with a personal access token.'
  },
  password: {
    label: 'Username & password',
    icon: Lock,
    blurb: 'Credentials stored encrypted in the vault.'
  },
  connection_string: {
    label: 'Connection string',
    icon: Terminal,
    blurb: 'Connect via a database/service connection string.'
  },
  browser_session: {
    label: 'Browser login',
    icon: Globe,
    blurb: 'Giga logs in through a secure browser session.'
  },
  jwt_byo: {
    label: 'JWT (bring your own)',
    icon: KeyRound,
    blurb: 'Sign requests with your own key.'
  },
  cli: {
    label: 'CLI',
    icon: Terminal,
    blurb: 'Authorize through the service CLI.'
  }
};
function methodMeta(key: string) {
  return METHOD_META[key] || {
    label: key,
    icon: Plug,
    blurb: ''
  };
}
/**
 * The page's shared vocabulary, so every section is the same object.
 *
 * It previously mixed three grounds (#FFFFFF, #FAFAFA, #FAF8F4) with cool
 * neutral-200 hairlines and #333333 type — a generic light theme that shared
 * nothing with the rest of the site. One cream ground, warm hairlines, ink
 * type, and the home page's 22px card radius and two-part shadow.
 */
const CARD = 'rounded-[22px] border border-[#E7E0D4] bg-white p-6 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-44px_rgba(43,33,26,0.35)] md:p-8';
const H2 = 'font-title text-[32px] font-bold leading-[1.06] tracking-[-0.03em] text-[#2B211A] md:text-[44px]';
const LEAD = 'mt-4 max-w-[58ch] text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]';
export function IntegrationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Seed from the build-time prime cache when present (SSR/prerender): undefined
  // = not primed (client fetches), null = known-missing, object = ready.
  const primed = slug ? getCachedIntegration(slug) : undefined;
  const [data, setData] = useState<Detail | null>(primed ?? null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound' | 'error'>(primed === undefined ? 'loading' : primed === null ? 'notfound' : 'ready');
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setStatus('loading');
    fetchIntegration(slug).then(d => {
      if (cancelled) return;
      if (!d) {
        setStatus('notfound');
        return;
      }
      setData(d);
      setStatus('ready');
    }).catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Set <title> + meta description from the catalog (baked in by the prerenderer).
  useEffect(() => {
    if (!data) return;
    document.title = data.seo_title || `${data.display_name} Integration — Giga`;
    const desc = data.seo_description || data.tagline || data.description || `Connect ${data.display_name} to Giga and use it through Slack.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc);
  }, [data]);
  if (status === 'loading') {
    return <div data-loading="true" className="flex min-h-screen items-center justify-center bg-[#FAF8F4] text-[#8A7B6D]" data-id="element-10691" data-mp="2k5z8p1w452tj">
        Loading…
      </div>;
  }
  if (status === 'notfound' || status === 'error') {
    return <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FAF8F4] px-6 text-center" data-id="element-10692" data-mp="cml8ryxn9veu">
        <h1 className="font-title text-[32px] font-bold tracking-[-0.03em] text-[#2B211A]" data-id="element-10693" data-mp="3s6ykarz1b2l">
          {status === 'notfound' ? 'Integration not found' : 'Something went wrong'}
        </h1>
        <p className="text-[#6B5D52]" data-id="element-10694" data-mp="3uwnh0z96mvt0">
          {status === 'notfound' ? "We couldn't find that integration." : "We couldn't load this integration right now."}
        </p>
        <Link to="/integrations" className="inline-flex items-center gap-1.5 font-semibold text-[#EA580C] hover:underline" data-id="element-10695" data-mp="3kyqz9olqp2lx">
          View all integrations <span aria-hidden="true" data-id="element-10696" data-mp="3bzvri831rkgi">→</span>
        </Link>
      </div>;
  }
  const d = data!;
  const pc = d.page_content || {};
  const features = pc.features || [];
  const prompts = pc.example_prompts || [];
  const faqs = pc.faqs || [];
  const heading = d.tagline || `Use ${d.display_name} through Slack with Giga.`;
  return <div className="min-h-screen bg-[#FAF8F4] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-10697" data-mp="1tcm7d86hyhm2">
      {/* Hero. Left-aligned like every other hero on the site, on the cream
       paper ground rather than a near-white one, and at the site's display
       sizes — 72px was a size nothing else on Giga uses. */}
      <section className="relative w-full overflow-hidden pb-16 pt-32 md:pb-24 md:pt-40" data-id="element-10698" data-mp="1pbses1i40emu">
        <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.10]" data-id="element-10699" data-mp="2d26ac1zwkvm6" />
        <div className="relative z-10 mx-auto max-w-[1120px] px-6 md:px-10" data-id="element-10700" data-mp="2h1wz605vd1o2">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-col items-start" data-id="element-10701" data-mp="231m7iuwqjj1u">
            <Link to="/integrations" className="mb-7 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#8A7B6D] transition-colors hover:text-[#2B211A]" data-id="element-10702" data-mp="wmbszmozvvzl">
              <span aria-hidden="true" data-id="element-10703" data-mp="p56u4u8w7ujs">←</span> All integrations
            </Link>

            {/* BOTH SIDES GET A MARK AND BOTH GET BOLD. The lockup used to set
             the partner's name in bold beside its logo and then leave "Giga"
             as plain text with nothing next to it — so on our own site the
             other brand outranked us in the one place the two are stated as
             equals. Same weight, same treatment, one mark each. */}
            <div className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-[#E7E0D4] bg-white py-2 pl-4 pr-5 shadow-[0_1px_2px_rgba(43,33,26,0.04)]" data-id="element-10704" data-mp="1ejd9jh01ul3">
              <IntegrationLogo slug={d.slug} name={d.display_name} logoUrl={d.logo_url} className="size-5" rounded="rounded" data-id="element-10705" data-mp="aeg0rhrnknek" />
              <span className="text-[13.5px] font-bold text-[#2B211A]" data-id="element-10706" data-mp="3isy79f1y3uw2">
                {d.display_name}
              </span>
              <span aria-hidden="true" className="text-[13px] text-[#A0917F]" data-id="element-10707" data-mp="3dumx137qnidb">
                ×
              </span>
              <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-5 flex-none object-contain" data-id="element-10708" data-mp="2tbfqbwx7wmgo" />
              <span className="text-[13.5px] font-bold text-[#2B211A]" data-id="element-10709" data-mp="329rezteixa6h">Giga</span>
            </div>

            <h1 className="max-w-[20ch] font-title text-[40px] font-bold leading-[1.04] tracking-[-0.03em] text-[#2B211A] sm:text-[52px] md:text-[60px]" data-id="element-10710" data-mp="378ukfeyf1iez">
              {heading}
            </h1>

            {d.description && <p className="mt-6 max-w-[60ch] text-[17px] leading-[1.7] text-[#6B5D52] md:text-[18.5px]" data-id="element-10711" data-mp="3g9aninb8ynjb">
                {d.description}
              </p>}

            <div className="mt-9 flex flex-col items-stretch gap-x-8 gap-y-4 sm:flex-row sm:items-center" data-id="element-10712" data-mp="3l10zsytqvldd">
              <a href={withAnalyticsParams('https://app.getgiga.com/signup')} onClick={() => trackSignupClick('integration-detail', `Connect ${d.display_name}`)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] px-8 py-3.5 text-[15px] font-bold text-white transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#C2410C]" data-id="element-10713" data-mp="3q1gbllydokt8">
                Connect {d.display_name}
                <ArrowRight size={16} data-id="element-10714" data-mp="1ebgvemxzd3di" />
              </a>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:text-[#EA580C]" data-id="element-10715" data-mp="1ec8ptufucv27">
                Talk to sales
              </a>
            </div>
            <p className="mt-7 text-[13px] text-[#8A7B6D]" data-id="element-10716" data-mp="223nz6761ccmy">
              $100 in credits free · No card required
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      {features.length > 0 && <section className="w-full border-t border-[#E7E0D4] bg-white py-20 md:py-28" data-id="element-10717" data-mp="2pr5lvoag0kj2">
          <div className="mx-auto max-w-[1120px] px-6 md:px-10" data-id="element-10718" data-mp="2pt02iqeolqhc">
            <div className="mb-12 md:mb-16" data-id="element-10719" data-mp="2kyvzgi8je2lj">
              <h2 className={H2} data-id="element-10720" data-mp="nnypnp0j4scc">
                Everything {d.display_name}, right in Slack.
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 md:gap-6" data-id="element-10721" data-mp="jnok2dgng3y0">
              {features.map((feature, i) => {
            const Icon = iconFor(feature.icon);
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="flex flex-col rounded-[22px] border border-[#E7E0D4] bg-[#FAF8F4] p-6 md:p-7" data-id="element-10722" data-mp="sn1ub8clkwjs" data-mpi={i}>
                    <Icon size={22} strokeWidth={1.9} className="mb-5 text-[#EA580C]" data-id="element-10723" data-mp="1bf45w5adtmn6" />
                    <h3 className="text-[17px] font-bold text-[#2B211A]" data-id="element-10724" data-mp="1qc4zyfg1ptot">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-[14.5px] leading-[1.65] text-[#6B5D52]" data-id="element-10725" data-mp="1l9w2p6w545gw">
                      {feature.description}
                    </p>
                  </motion.div>;
          })}
            </div>
          </div>
        </section>}

      {/* Example prompts */}
      {prompts.length > 0 && <section className="w-full border-t border-[#E7E0D4] bg-[#FAF8F4] py-20 md:py-28" data-id="element-10726" data-mp="1uzcuk22vdx0a">
          <div className="mx-auto max-w-[1120px] px-6 md:px-10" data-id="element-10727" data-mp="1zwursv7iynsg">
            <div className="mb-12 md:mb-16" data-id="element-10728" data-mp="1l9jeoovp2vq8">
              <h2 className={H2} data-id="element-10729" data-mp="1l7owhibt5hpu">See it in action.</h2>
              <p className={LEAD} data-id="element-10730" data-mp="1q1jnz7xnn0zh">
                Ask in plain language, in the thread you are already in.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6" data-id="element-10731" data-mp="1c70holelbdbw">
              {prompts.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className={CARD} data-id="element-10732" data-mp="1h63j7vvd31f4" data-mpi={i}>
                  <div className="flex items-start gap-3" data-id="element-10733" data-mp="jeoryj8md2k9">
                    <MessageSquare size={16} strokeWidth={2} className="mt-[3px] shrink-0 text-[#EA580C]" data-id="element-10734" data-mp="jddrasah0axd" />
                    <p className="text-[15px] font-semibold leading-snug text-[#2B211A]" data-id="element-10735" data-mp="efvj544a1zqf">
                      {item.prompt}
                    </p>
                  </div>
                  <p className="mt-4 border-t border-[#EFE9DF] pt-4 text-[14.5px] leading-[1.65] text-[#6B5D52]" data-id="element-10736" data-mp="172yclhu3jk9t">
                    {item.result}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Connection methods */}
      {d.methods && d.methods.length > 0 && <section className="w-full border-t border-[#E7E0D4] bg-white py-20 md:py-28" data-id="element-10737" data-mp="s6qt6jqiimvl">
          <div className="mx-auto max-w-[1120px] px-6 md:px-10" data-id="element-10738" data-mp="3hxvrikdqw74k">
            <div className="mb-12 md:mb-16" data-id="element-10739" data-mp="3cwpuiad1xxp8">
              <h2 className={H2} data-id="element-10740" data-mp="3cvwo5ai09a7m">Connect the way that suits you.</h2>
            </div>
            <div className="max-w-3xl space-y-4" data-id="element-10741" data-mp="1sgrtow188k14">
              {d.methods.map((m: CatalogMethod) => {
            const meta = methodMeta(m.key);
            const Icon = meta.icon;
            const recommended = d.recommended_method === m.key || m.preferred;
            const body = m.setup_instructions || meta.blurb || m.description || '';
            return <div key={m.key} className="rounded-[22px] border border-[#E7E0D4] bg-[#FAF8F4] p-6 md:p-7" data-id="element-10742" data-mp="2c3g12869clfy">
                    <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-10743" data-mp="2g489x0b7wobp">
                      <Icon size={18} strokeWidth={1.9} className="shrink-0 text-[#EA580C]" data-id="element-10744" data-mp="2l3uobd2s8am3" />
                      <h3 className="text-[16.5px] font-bold text-[#2B211A]" data-id="element-10745" data-mp="2q2xjm5syn2gv">
                        {meta.label}
                      </h3>
                      {recommended && <span className="rounded-full bg-[#EA580C]/10 px-2.5 py-1 text-[11px] font-bold text-[#C2410C]" data-id="element-10746" data-mp="qrebftfhdy89">
                          Recommended
                        </span>}
                      {m.doc_url && <a href={m.doc_url} target="_blank" rel="noopener noreferrer" className="ml-auto shrink-0 text-[13.5px] font-semibold text-[#EA580C] hover:underline" data-id="element-10747" data-mp="2m4grrxzmxwya">
                          Docs →
                        </a>}
                    </div>
                    {body && <div className="text-sm" data-id="element-10748" data-mp="2r4v1rlksrgyy">
                        <Markdown data-id="element-10749" data-mp="2r30cxw6en4pl">{body}</Markdown>
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* FAQ */}
      {faqs.length > 0 && <section className="w-full border-t border-[#E7E0D4] bg-[#FAF8F4] py-20 md:py-28" data-id="element-10750" data-mp="1yecoxsu9u18c">
          <div className="mx-auto max-w-[860px] px-6 md:px-10" data-id="element-10751" data-mp="2c95bhb8kwhof">
            <h2 className={`${H2} mb-10`} data-id="element-10752" data-mp="27g2q3qdygejb">Common questions.</h2>
            <div className="divide-y divide-[#E7E0D4] border-y border-[#E7E0D4]" data-id="element-10753" data-mp="1jrjf333itnnj">
              {faqs.map((item, i) => <details key={i} className="group" data-id="element-10754" data-mp="1fq7n4qx6cqc6" data-mpi={i}>
                  <summary className="flex cursor-pointer items-center justify-between gap-4 py-5 text-[16.5px] font-bold text-[#2B211A] transition-colors hover:text-[#EA580C]" data-id="element-10755" data-mp="1fpg22a17eb05">
                    {item.q}
                    <span className="shrink-0 text-[22px] leading-none text-[#EA580C] transition-transform duration-200 ease-out group-open:rotate-45" data-id="element-10756" data-mp="1ae2d1h149p8u">
                      +
                    </span>
                  </summary>
                  <div className="pb-6 text-[14.5px] leading-[1.7] text-[#6B5D52]" data-id="element-10757" data-mp="1ocip7nhr07yd">
                    <Markdown data-id="element-10758" data-mp="1oao6mfty8rxg">{item.a}</Markdown>
                  </div>
                </details>)}
            </div>
          </div>
        </section>}

      <ClosingCTA data-id="element-10759" data-mp="1edb3od0v1706" />
    </div>;
}